import { useEffect, useMemo, useState } from 'react';

/**
 * ツリー表示（プレオーダー平坦リスト）の状態を扱うフック（FKV3-12858 / FKV3-12941）
 *
 * 倉庫を選ぶツリー（`WarehouseMultiSelect`）・集計する店舗グループを選ぶツリー
 * （`StoreGroupExclusiveSelect`）・商品グループを選ぶツリー（`ProductGroupTreeMultiSelect`）で
 * 選択の意味は違うが、絞り込みと開閉の挙動は同じなのでここに集約する。
 *
 * 呼び出し側は自分のノード型を `TreeViewNode` へ写して渡す。
 * 写した配列は useMemo で安定させること（毎レンダー新しい配列を渡すと開閉状態がリセットされる）。
 */

/** 表示制御に必要な最小のノード情報（配列順＝表示順のプレオーダー） */
export interface TreeViewNode {
  name: string;
  /** 階層（1始まり）。親の直後に level の大きい配下が続く */
  level: number;
  /** 子を持つノードか（検索時に配下ごと表示する対象） */
  isGroup: boolean;
}
export interface TreeViewState {
  search: string;
  setSearch: (value: string) => void;
  /** 検索で表示するノードのindex集合（null＝検索なしで全表示） */
  visibleIndexes: Set<number> | null;
  /** 折りたたみ中の親ノードの配下index集合 */
  hiddenByCollapse: Set<number>;
  collapsedGroups: Set<number>;
  toggleCollapse: (index: number) => void;
  /** 検索結果が1件でもあるか（0件表示の切り替え用） */
  hasVisibleNodes: boolean;
}

export function useTreeView(
  nodes: TreeViewNode[],
  options: { groupsOnly?: boolean } = {},
): TreeViewState {
  const { groupsOnly = false } = options;
  const [search, setSearch] = useState('');
  // 折りたたみ中の親ノードのindex集合（既定は全展開。プロダクトの倉庫ツリーに合わせる）
  const [collapsedGroups, setCollapsedGroups] = useState<Set<number>>(new Set());

  // ツリーが差し替わったら（アカウント切替等）折りたたみ状態をリセットする
  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [nodes]);

  // 検索で表示するノードのindex集合（null=検索なしで全表示）。
  // 末端の一致は自身＋祖先、親ノードの一致は自身＋祖先＋配下すべてを表示する。
  // 親しか選べないツリーでは末端名で絞っても結果が見えないため、親の名前だけを対象にする
  const visibleIndexes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;

    const visible = new Set<number>();
    const ancestorStack: number[] = []; // 現在のノードの祖先グループindex（levelの浅い順）
    let matchedGroupLevel: number | null = null; // グループ名一致した部分木の起点level

    nodes.forEach((node, index) => {
      while (
        ancestorStack.length > 0 &&
        nodes[ancestorStack[ancestorStack.length - 1]].level >= node.level
      ) {
        ancestorStack.pop();
      }
      if (matchedGroupLevel !== null && node.level <= matchedGroupLevel) {
        matchedGroupLevel = null; // 一致部分木を抜けた
      }

      const matched = (!groupsOnly || node.isGroup) && node.name.toLowerCase().includes(q);
      if (matchedGroupLevel !== null || matched) {
        visible.add(index);
        ancestorStack.forEach((i) => visible.add(i));
        if (matched && node.isGroup && matchedGroupLevel === null) {
          matchedGroupLevel = node.level;
        }
      }

      if (node.isGroup) ancestorStack.push(index);
    });
    return visible;
  }, [nodes, search, groupsOnly]);

  // 折りたたみ中グループの配下ノードindex集合（検索中は無効＝検索結果を優先して自動展開扱い）
  const hiddenByCollapse = useMemo(() => {
    const hidden = new Set<number>();
    collapsedGroups.forEach((groupIndex) => {
      const group = nodes[groupIndex];
      if (!group) return;
      for (let i = groupIndex + 1; i < nodes.length && nodes[i].level > group.level; i++) {
        hidden.add(i);
      }
    });
    return hidden;
  }, [nodes, collapsedGroups]);

  const toggleCollapse = (index: number) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return {
    search,
    setSearch,
    visibleIndexes,
    hiddenByCollapse,
    collapsedGroups,
    toggleCollapse,
    hasVisibleNodes: visibleIndexes === null || visibleIndexes.size > 0,
  };
}
